import { useState, useEffect } from 'react'
import { Loader2, X, ExternalLink, Image as ImageIcon } from 'lucide-react'
import { Button } from './Button'
import { Bookmark } from '../types/bookmark'

interface BookmarkPreviewProps {
  bookmark: Bookmark
  onClose: () => void
}

interface PreviewMetadata {
  favicon?: string
  image?: string
  description?: string
  title?: string
}

export function BookmarkPreview({ bookmark, onClose }: BookmarkPreviewProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [metadata, setMetadata] = useState<PreviewMetadata>({})

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const response = await fetch('/api/preview-metadata', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ url: bookmark.url }),
        })
        
        // 检查响应状态
        if (!response.ok) {
          throw new Error(`HTTP 错误: ${response.status}`)
        }
        
        // 尝试解析 JSON，添加错误处理
        let data
        try {
          data = await response.json()
        } catch (jsonError) {
          console.error('JSON 解析错误:', jsonError)
          throw new Error('无法解析服务器响应')
        }
        
        if (data.error) {
          throw new Error(data.error)
        }
        setMetadata(data)
      } catch (error) {
        console.error('预览加载错误:', error)
        setError(error instanceof Error ? error.message : '获取预览信息失败')
      }
    }

    fetchMetadata()
  }, [bookmark.url])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* 预览头部 */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {metadata.favicon ? (
              <img
                src={metadata.favicon}
                alt="favicon"
                className="w-4 h-4"
                onError={(e) => {
                  e.currentTarget.src = ''
                  e.currentTarget.className = 'hidden'
                }}
              />
            ) : (
              <ImageIcon className="w-4 h-4 text-gray-400" />
            )}
            <h2 className="text-lg font-medium truncate">
              {metadata.title || bookmark.title}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(bookmark.url, '_blank')}
              className="flex items-center"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              在新标签页打开
            </Button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 预览内容 */}
        <div className="flex-1 min-h-0 flex flex-col">
          {metadata.description && (
            <div className="p-4 border-b bg-gray-50">
              <p className="text-sm text-gray-600">{metadata.description}</p>
            </div>
          )}
          
          <div className="relative flex-1">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            )}
            {error ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                <div className="text-red-500 text-center">
                  <p className="font-medium">预览加载失败</p>
                  <p className="text-sm mt-1">{error}</p>
                </div>
              </div>
            ) : (
              <iframe
                src={bookmark.url}
                className="w-full h-full border-0"
                onLoad={() => setLoading(false)}
                onError={() => {
                  setLoading(false)
                  setError('无法加载预览')
                }}
                sandbox="allow-scripts allow-same-origin"
                title={metadata.title || bookmark.title}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
} 